import { useState } from 'react';
import { waTemplates, waSentMessages, waKPIs } from '../../mocks/whatsapp';
import type { WaTemplate } from '../../mocks/whatsapp';
import WhatsAppTemplateCard from './components/WhatsAppTemplateCard';
import MessageComposer from './components/MessageComposer';
import MessageHistory from './components/MessageHistory';
import TemplatePreviewModal from './components/TemplatePreviewModal';

type Tab = 'templates' | 'compose' | 'history';

export default function WhatsAppPage() {
  const [activeTab, setActiveTab] = useState<Tab>('templates');
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerTemplate, setComposerTemplate] = useState<WaTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<WaTemplate | null>(null);
  const [sentCount, setSentCount] = useState(waSentMessages.length);
  const [successBanner, setSuccessBanner] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('All');

  const categories = ['All', 'Booking', 'Payment', 'Reminder', 'Visa', 'Custom'];

  const filteredTemplates = waTemplates.filter(
    (t) => categoryFilter === 'All' || t.category === categoryFilter
  );

  const openComposer = (template?: WaTemplate) => {
    setComposerTemplate(template || null);
    setComposerOpen(true);
  };

  const handleSent = () => {
    setComposerOpen(false);
    setSentCount((c) => c + 1);
    setSuccessBanner(true);
    setActiveTab('history');
    setTimeout(() => setSuccessBanner(false), 4000);
  };

  const handleResend = (_id: string) => {
    openComposer();
  };

  const handlePreviewUse = () => {
    const t = previewTemplate;
    setPreviewTemplate(null);
    openComposer(t || undefined);
  };

  const kpiCards = [
    {
      label: 'Messages Sent',
      value: sentCount.toLocaleString(),
      sub: `Monthly limit: ${waKPIs.monthlyLimit}`,
      icon: 'ri-send-plane-2-line',
      color: '#25D366',
    },
    {
      label: 'Delivery Rate',
      value: `${waKPIs.deliveryRate}%`,
      sub: 'Messages delivered',
      icon: 'ri-check-double-line',
      color: '#10b981',
    },
    {
      label: 'Read Rate',
      value: `${waKPIs.readRate}%`,
      sub: 'Opened by recipients',
      icon: 'ri-eye-line',
      color: '#6366f1',
    },
    {
      label: 'Response Rate',
      value: `${waKPIs.responseRate}%`,
      sub: 'Customers replied',
      icon: 'ri-chat-smile-2-line',
      color: '#f59e0b',
    },
  ];

  const quickSendTemplates = [
    { id: 'tpl-1', label: 'Booking Confirmation', icon: 'ri-calendar-check-line', color: '#10b981' },
    { id: 'tpl-2', label: 'Payment Reminder', icon: 'ri-money-dollar-circle-line', color: '#f59e0b' },
    { id: 'tpl-3', label: 'Payment Received', icon: 'ri-checkbox-circle-line', color: '#10b981' },
    { id: 'tpl-4', label: 'Pre-Departure', icon: 'ri-flight-takeoff-line', color: '#6366f1' },
    { id: 'tpl-5', label: 'Visa Approved', icon: 'ri-passport-line', color: '#8b5cf6' },
    { id: 'tpl-6', label: 'Post-Trip Follow Up', icon: 'ri-star-smile-line', color: '#f43f5e' },
  ];

  return (
    <div className="p-6 lg:p-8 min-h-screen bg-[#f9fafb]">
      {/* Success Banner */}
      {successBanner && (
        <div className="fixed top-6 right-6 z-50 flex items-center gap-3 bg-white border border-green-200 text-gray-800 text-sm px-4 py-3 rounded-xl shadow-md">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#25D36618' }}>
            <i className="ri-whatsapp-line" style={{ color: '#25D366' }} />
          </div>
          <span>WhatsApp message sent successfully!</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#25D36618' }}>
            <i className="ri-whatsapp-line text-xl" style={{ color: '#25D366' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">WhatsApp Integration</h1>
            <p className="text-sm text-gray-400 mt-0.5">Send booking confirmations, payment reminders & more via WhatsApp</p>
          </div>
        </div>
        <button
          onClick={() => openComposer()}
          className="flex items-center gap-2 px-4 py-2.5 text-sm text-white font-medium rounded-xl cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: '#25D366' }}
        >
          <i className="ri-send-plane-fill" />
          Send Message
        </button>
      </div>

      {/* Monthly Usage Bar */}
      <div className="bg-white border border-gray-100 rounded-xl p-4 mb-6 flex items-center gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#25D36618' }}>
            <i className="ri-bar-chart-horizontal-line text-sm" style={{ color: '#25D366' }} />
          </div>
          <span className="text-sm font-medium text-gray-700">Monthly Usage</span>
        </div>
        <div className="flex-1">
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all"
              style={{ width: `${(waKPIs.monthlyUsed / waKPIs.monthlyLimit) * 100}%`, backgroundColor: '#25D366' }}
            />
          </div>
        </div>
        <span className="text-sm font-semibold text-gray-700 flex-shrink-0 whitespace-nowrap">
          {waKPIs.monthlyUsed} / {waKPIs.monthlyLimit}
          <span className="text-xs text-gray-400 font-normal ml-1">messages</span>
        </span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpiCards.map((k) => (
          <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium text-gray-500">{k.label}</p>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${k.color}14` }}>
                <i className={`${k.icon} text-base`} style={{ color: k.color }} />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800 leading-none">{k.value}</p>
            <p className="text-xs text-gray-400 mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl w-fit mb-6">
        {(['templates', 'compose', 'history'] as Tab[]).map((t) => {
          const icons: Record<Tab, string> = {
            templates: 'ri-layout-grid-line',
            compose: 'ri-send-plane-line',
            history: 'ri-history-line',
          };
          const labels: Record<Tab, string> = {
            templates: 'Templates',
            compose: 'Quick Send',
            history: `Sent History`,
          };
          return (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === t ? 'bg-white text-gray-800' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${icons[t]} text-base`} />
              {labels[t]}
              {t === 'history' && (
                <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full font-medium">
                  {sentCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab: Templates */}
      {activeTab === 'templates' && (
        <div>
          {/* Category filter */}
          <div className="flex items-center gap-2 flex-wrap mb-5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  categoryFilter === cat
                    ? 'text-white border-transparent'
                    : 'text-gray-500 border-gray-200 bg-white hover:bg-gray-50'
                }`}
                style={categoryFilter === cat ? { backgroundColor: '#25D366', borderColor: '#25D366' } : {}}
              >
                {cat}
                {cat !== 'All' && (
                  <span className="ml-1 opacity-70">({waTemplates.filter((t) => t.category === cat).length})</span>
                )}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((t) => (
              <WhatsAppTemplateCard
                key={t.id}
                template={t}
                onPreview={(tpl) => setPreviewTemplate(tpl)}
                onUse={(tpl) => openComposer(tpl)}
              />
            ))}
          </div>

          {/* WhatsApp Business Setup Notice */}
          <div className="mt-8 bg-white border border-gray-100 rounded-xl p-5 flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#25D36618' }}>
              <i className="ri-information-line text-lg" style={{ color: '#25D366' }} />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-800 mb-1">WhatsApp Business API Connected</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Your agency is connected to WhatsApp Business API. All message templates have been approved by Meta. 
                Messages are sent from your verified business number <span className="font-medium text-gray-700">+971 4 200 1234</span> (Horizon Travel & Tourism).
              </p>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-emerald-500" />
                  </div>
                  Meta Verified
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-double-line text-emerald-500" />
                  </div>
                  {waKPIs.activeTemplates} Active Templates
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-signal-wifi-3-line text-emerald-500" />
                  </div>
                  API Status: Online
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Quick Send */}
      {activeTab === 'compose' && (
        <div>
          <p className="text-sm text-gray-500 mb-5">Choose a template to quickly send to any customer:</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {quickSendTemplates.map((qt) => {
              const tpl = waTemplates.find((t) => t.id === qt.id);
              return (
                <button
                  key={qt.id}
                  onClick={() => tpl && openComposer(tpl)}
                  className="flex flex-col items-center gap-2.5 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-300 transition-all cursor-pointer group"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${qt.color}18` }}
                  >
                    <i className={`${qt.icon} text-2xl`} style={{ color: qt.color }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700 text-center leading-tight">{qt.label}</span>
                </button>
              );
            })}
          </div>

          {/* Bulk Send section */}
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Bulk Message Campaigns</h3>
                <p className="text-xs text-gray-400 mt-0.5">Send one message to multiple customers at once</p>
              </div>
              <span className="text-xs px-2.5 py-1 bg-amber-50 text-amber-700 rounded-full font-medium">Coming Soon</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'Payment Overdue Campaign', desc: 'Send reminders to all customers with overdue balances', icon: 'ri-money-dollar-circle-line', count: '5 customers' },
                { label: 'Pre-Departure Batch', desc: 'Auto-send departure reminders 48h before travel', icon: 'ri-flight-takeoff-line', count: '3 departing' },
                { label: 'Post-Trip Follow Up', desc: 'Request reviews from customers who returned this week', icon: 'ri-star-smile-line', count: '8 returned' },
              ].map((item) => (
                <div key={item.label} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <i className={`${item.icon} text-lg text-gray-400`} />
                    <span className="text-xs bg-white border border-gray-200 text-gray-500 px-2 py-0.5 rounded-full">{item.count}</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-700 mb-1">{item.label}</p>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab: History */}
      {activeTab === 'history' && (
        <MessageHistory onResend={handleResend} />
      )}

      {/* Composer Drawer */}
      {composerOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setComposerOpen(false)} />
          <div className="relative ml-auto w-full max-w-lg bg-white h-full flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#25D36618' }}>
                  <i className="ri-whatsapp-line text-base" style={{ color: '#25D366' }} />
                </div>
                <h2 className="text-sm font-semibold text-gray-800">Send WhatsApp Message</h2>
              </div>
              <button onClick={() => setComposerOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <MessageComposer
                initialTemplate={composerTemplate}
                onClose={() => setComposerOpen(false)}
                onSent={handleSent}
              />
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onUse={handlePreviewUse}
        />
      )}
    </div>
  );
}
